import { useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { get } from "common/helpers/HTTP";

import useLeadConversationUrn from "./useLeadConversationUrn";

import type { Conversation } from "../types";

async function getConversationRequest(
  workspaceId: string,
  conversationUrn: string,
): Promise<Conversation> {
  return (
    await get(`workspaces/${workspaceId}/conversations/${conversationUrn}`)
  ).conversation;
}

type ConversationParams = {
  accountId: string;
} & (
  | { leadId: string }
  | { conversationUrn: string; initialConversation?: Conversation }
);

export default function useConversation(
  conversationParams: ConversationParams | undefined,
) {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { conversationsKeys } = getQueryKeys(workspaceId);

  const leadConversationUrn = useLeadConversationUrn(
    conversationParams && "leadId" in conversationParams && conversationParams,
  );

  const conversationUrn =
    conversationParams && "conversationUrn" in conversationParams
      ? conversationParams.conversationUrn
      : leadConversationUrn;

  const initialData =
    conversationParams &&
    "initialConversation" in conversationParams &&
    conversationParams.initialConversation;

  const {
    data: conversation,
    isLoading: isLoadingConversation,
    refetch: refetchConversation,
  } = useQuery({
    queryKey: conversationParams
      ? conversationsKeys.details(conversationUrn, conversationParams.accountId)
      : undefined,
    queryFn: conversationUrn
      ? () => getConversationRequest(workspaceId, conversationUrn)
      : undefined,
    enabled: !initialData,
    initialData,
  });

  return {
    conversation,
    isLoadingConversation,
    refetchConversation,
  };
}
